import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import type { PlayerKanbanPlayerFragmentFragment } from "@/generated/graphql";

import PlayerListSection from "../PlayerListSection";

/** Props for {@link AddPlayersSourceListView}. */
interface AddPlayersSourceListViewProps {
  /** Players in the pool, already filtered. */
  readonly players: PlayerKanbanPlayerFragmentFragment[];
  /** Players already placed on the board, by id. */
  readonly playersOnKanban: Record<
    string,
    { phaseId: string; phaseName: string }
  >;
  /** Whether the pool is loading. */
  readonly isLoadingPlayers: boolean;
  /** Pool read error, or `null`. */
  readonly playersError: string | null;
  /** Which pool the cards render, driving the meta line (SE-6952). */
  readonly metaVariant: "watchlist" | "roster";
  /** Per-card one-click add (SE-6951). */
  readonly onAdd: (playerId: string) => void;
  /** Player ids with an add in flight (SE-6951). */
  readonly addingPlayerIds: readonly string[];
  /** Open a player. */
  readonly onPlayerClick: (playerId: string) => void;
  /** Drag start callback. */
  readonly onGlobalDragStart?: (dragData?: unknown, event?: unknown) => void;
  /** Drag end callback. */
  readonly onGlobalDragEnd?: () => void;
  /** Auto-scroll callback. */
  readonly onAutoScroll?: (dragX: number) => void;
  /** Load the next page. */
  readonly onLoadMore?: () => void;
}

/**
 * Watchlists / My Roster tab body — the shared, draggable player list.
 *
 * ## The watchlist picker is GONE from this body (SE-6554)
 *
 * It used to sit above the list as an "All Watchlists" select. Its job now
 * belongs to the drawer's Filters menu, which multi-selects watchlists from the
 * panel chrome, so keeping a second single-select here would have given the tab
 * two controls that disagree. With it gone, the two pools differ only in the
 * QUERY the container runs, so this body no longer takes `source` at all.
 * @param root0 - Component props.
 * @param root0.players - Players in the pool.
 * @param root0.playersOnKanban - Players already on the board.
 * @param root0.isLoadingPlayers - Whether the pool is loading.
 * @param root0.playersError - Pool read error.
 * @param root0.metaVariant - Which pool the cards render (SE-6952).
 * @param root0.onAdd - Per-card add callback (SE-6951).
 * @param root0.addingPlayerIds - Player ids with an add in flight (SE-6951).
 * @param root0.onPlayerClick - Player click callback.
 * @param root0.onGlobalDragStart - Drag start callback.
 * @param root0.onGlobalDragEnd - Drag end callback.
 * @param root0.onAutoScroll - Auto-scroll callback.
 * @param root0.onLoadMore - Load-more callback.
 */
const AddPlayersSourceListView = ({
  players,
  playersOnKanban,
  isLoadingPlayers,
  playersError,
  metaVariant,
  onAdd,
  addingPlayerIds,
  onPlayerClick,
  onGlobalDragStart,
  onGlobalDragEnd,
  onAutoScroll,
  onLoadMore,
}: AddPlayersSourceListViewProps) => (
  <Stack className="flex-1 gap-2">
    {isLoadingPlayers ? (
      <Box className="flex-1 items-center justify-center p-4">
        <Spinner size="small" />
      </Box>
    ) : playersError ? (
      <Box className="flex-1 items-center justify-center p-4">
        <Text variant="caption" className="text-center text-status-error">
          {playersError}
        </Text>
      </Box>
    ) : (
      <PlayerListSection
        variant="drawer"
        players={players}
        playersOnKanban={playersOnKanban}
        metaVariant={metaVariant}
        onAddPlayer={onAdd}
        addingPlayerIds={addingPlayerIds}
        onPlayerClick={onPlayerClick}
        onGlobalDragStart={onGlobalDragStart}
        onGlobalDragEnd={onGlobalDragEnd}
        onAutoScroll={onAutoScroll}
        onLoadMore={onLoadMore}
      />
    )}
  </Stack>
);

AddPlayersSourceListView.displayName = "AddPlayersSourceListView";

export default memo(AddPlayersSourceListView);
